import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import activoService from '../../../services/activoService';
import { getAreas } from '../../../services/areaService';
import { findFamilyPath, STATUS_OPTIONS } from '../Listado/ActivoForm';
import { formatDateLocaleLima } from '../../../utils/dateUtils';
import Toast from '../../../components/Common/Toast';

const NO_AREA_KEY = '__sin_area__';

const formatDate = (value) => (value ? formatDateLocaleLima(value) || '—' : '—');

const getStatusLabel = (status) => STATUS_OPTIONS.find((o) => o.value === status)?.label || status;

const getStatusClasses = (status) => {
  if (status === 'pending_start') return 'bg-amber-50 text-amber-800';
  if (status === 'active') return 'bg-green-50 text-green-800';
  if (status === 'maintenance') return 'bg-orange-50 text-orange-800';
  if (status === 'inactive') return 'bg-gray-100 text-gray-700';
  if (status === 'retired') return 'bg-red-50 text-red-800';
  return 'bg-gray-100 text-gray-700';
};

/** Estado visual según la fecha consultada (activo dado de baja puede figurar en uso antes de esa fecha). */
const getDisplayStatus = (activo, asOfDate) => {
  if (activo.retired_at && asOfDate >= activo.retired_at.slice(0, 10)) {
    return 'retired';
  }
  if (activo.status === 'retired') {
    return 'active';
  }
  return activo.status;
};

const iconBtnClass =
  'inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors';

const IconFileText = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" />
  </svg>
);

const IconBarcode = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 5v14" /><path d="M8 5v14" /><path d="M12 5v14" /><path d="M17 5v14" /><path d="M21 5v14" />
  </svg>
);

const IconMoveArea = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M8 3 4 7l4 4" />
    <path d="M4 7h16" />
    <path d="m16 21 4-4-4-4" />
    <path d="M20 17H4" />
  </svg>
);

const ActivosEnUso = () => {
  const [activos, setActivos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [viewAsOf, setViewAsOf] = useState(() => new Date().toISOString().split('T')[0]);
  const [expandedAreas, setExpandedAreas] = useState({});
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [activoToMove, setActivoToMove] = useState(null);
  const [newAreaId, setNewAreaId] = useState('');
  const [savingMove, setSavingMove] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [activosRes, areasRes] = await Promise.all([
        activoService.getActivos({ in_use: true, as_of: viewAsOf }),
        getAreas({ active_only: true }),
      ]);
      if (activosRes.success) setActivos(activosRes.data);
      if (areasRes.success && Array.isArray(areasRes.data)) setAreas(areasRes.data);
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar activos en uso', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [viewAsOf]);

  const filteredActivos = useMemo(() => {
    return activos.filter((a) => {
      const matchesSearch =
        !searchTerm ||
        a.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.area?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesArea =
        !areaFilter ||
        (areaFilter === NO_AREA_KEY ? !a.area_id : String(a.area_id) === String(areaFilter));
      return matchesSearch && matchesArea;
    });
  }, [activos, searchTerm, areaFilter]);

  const groupedByArea = useMemo(() => {
    const groups = new Map();

    areas.forEach((area) => {
      groups.set(String(area.id), { area, activos: [] });
    });

    groups.set(NO_AREA_KEY, { area: null, activos: [] });

    filteredActivos.forEach((activo) => {
      const key = activo.area_id ? String(activo.area_id) : NO_AREA_KEY;
      if (!groups.has(key)) {
        groups.set(key, { area: activo.area || null, activos: [] });
      }
      groups.get(key).activos.push(activo);
    });

    const result = [];
    areas.forEach((area) => {
      const group = groups.get(String(area.id));
      if (group && (group.activos.length > 0 || !areaFilter)) {
        result.push(group);
      }
    });

    const sinArea = groups.get(NO_AREA_KEY);
    if (sinArea && sinArea.activos.length > 0) {
      result.push(sinArea);
    }

    return result;
  }, [areas, filteredActivos, areaFilter]);

  useEffect(() => {
    if (Object.keys(expandedAreas).length > 0) return;
    const initial = {};
    groupedByArea.forEach((g) => {
      const key = g.area?.id ? String(g.area.id) : NO_AREA_KEY;
      initial[key] = g.activos.length > 0;
    });
    setExpandedAreas(initial);
  }, [groupedByArea, expandedAreas]);

  const toggleArea = (key) => {
    setExpandedAreas((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openMoveModal = (activo) => {
    setActivoToMove(activo);
    setNewAreaId('');
    setMoveModalOpen(true);
  };

  const closeMoveModal = () => {
    setMoveModalOpen(false);
    setActivoToMove(null);
    setNewAreaId('');
  };

  const confirmMoveArea = async () => {
    if (!activoToMove || !newAreaId) return;
    setSavingMove(true);
    try {
      const response = await activoService.updateArea(activoToMove.id, parseInt(newAreaId, 10));
      if (response.success) {
        setToast({
          isOpen: true,
          message: `Activo trasladado a ${response.data?.area?.name || 'la nueva área'}`,
          type: 'success',
        });
        closeMoveModal();
        setExpandedAreas({});
        loadData();
      } else {
        setToast({ isOpen: true, message: response.message || 'No se pudo cambiar el área', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cambiar el área',
        type: 'error',
      });
    } finally {
      setSavingMove(false);
    }
  };

  const totalEnUso = filteredActivos.length;
  const areasConActivos = groupedByArea.filter((g) => g.activos.length > 0).length;

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Activos en uso por área</h1>
          <p className="text-gray-600 mt-1">
            Consulte los activos en uso agrupados por área al día seleccionado.
            Un activo dado de baja sigue apareciendo hasta su fecha de baja (inclusive).
          </p>
        </div>

        <nav className="text-sm text-gray-600 mb-4">
          <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
          <span className="mx-2">/</span>
          <Link to="/activos" className="hover:text-teal-600">Activos</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-800">En uso por áreas</span>
        </nav>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Activos en uso</p>
            <p className="text-2xl font-semibold text-teal-700">{totalEnUso}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Áreas con activos</p>
            <p className="text-2xl font-semibold text-gray-800">{areasConActivos}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-6 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Consultar al</label>
            <input
              type="date"
              value={viewAsOf}
              onChange={(e) => setViewAsOf(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <input
            type="text"
            placeholder="Buscar por código, nombre, marca, serie o área…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-md"
          />
          <select
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">Todas las áreas</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
            <option value={NO_AREA_KEY}>Sin área asignada</option>
          </select>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">Cargando…</div>
        ) : groupedByArea.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            No hay activos en uso que coincidan con los filtros
          </div>
        ) : (
          <div className="space-y-4">
            {groupedByArea.map((group) => {
              const areaKey = group.area?.id ? String(group.area.id) : NO_AREA_KEY;
              const areaName = group.area?.name || 'Sin área asignada';
              const isExpanded = expandedAreas[areaKey] !== false;
              const count = group.activos.length;

              return (
                <div key={areaKey} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                  <button
                    type="button"
                    onClick={() => toggleArea(areaKey)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-4 bg-gray-50 hover:bg-gray-100 text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xl" aria-hidden>🏢</span>
                      <div>
                        <h2 className="font-semibold text-gray-900">{areaName}</h2>
                        {group.area?.code ? (
                          <p className="text-xs text-gray-500">{group.area.code}</p>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-teal-50 text-teal-800 text-xs font-medium">
                        {count} activo{count !== 1 ? 's' : ''}
                      </span>
                      <span className="text-gray-400">{isExpanded ? '▾' : '▸'}</span>
                    </div>
                  </button>

                  {isExpanded && (
                    count === 0 ? (
                      <div className="px-6 py-6 text-sm text-gray-500 border-t border-gray-100">
                        No hay activos en uso en esta área.
                      </div>
                    ) : (
                      <div className="overflow-x-auto border-t border-gray-100">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clasificación</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serie / placa</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inicio actividades</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fin / baja</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vida útil</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {group.activos.map((activo) => {
                              const displayStatus = getDisplayStatus(activo, viewAsOf);
                              const canMove = displayStatus !== 'retired';

                              return (
                              <tr key={activo.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{activo.code}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{activo.name}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{findFamilyPath(activo.family)}</td>
                                <td className="px-6 py-4 text-sm text-gray-600">{activo.serial_number || '—'}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">
                                  {formatDate(activo.activity_start_date)}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700">
                                  {activo.retired_at ? formatDate(activo.retired_at) : '—'}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700">
                                  {activo.useful_life_years
                                    ? `${activo.useful_life_years} año${activo.useful_life_years !== 1 ? 's' : ''}`
                                    : '—'}
                                </td>
                                <td className="px-6 py-4 text-sm">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusClasses(displayStatus)}`}>
                                    {getStatusLabel(displayStatus)}
                                  </span>
                                  {activo.retired_at && viewAsOf < activo.retired_at.slice(0, 10) && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Baja: {formatDate(activo.retired_at)}
                                    </p>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-sm whitespace-nowrap">
                                  <div className="flex items-center gap-1">
                                    {canMove && (
                                    <button
                                      type="button"
                                      onClick={() => openMoveModal(activo)}
                                      className={`${iconBtnClass} text-blue-600 hover:text-blue-800 hover:bg-blue-50`}
                                      title="Cambiar área"
                                      aria-label="Cambiar área"
                                    >
                                      <IconMoveArea />
                                    </button>
                                    )}
                                    <Link
                                      to={`/activos/listado/${activo.id}/print`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={iconBtnClass}
                                      title="Ficha técnica"
                                      aria-label="Ficha técnica"
                                    >
                                      <IconFileText />
                                    </Link>
                                    <Link
                                      to={`/activos/listado/${activo.id}/print-barcode`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={iconBtnClass}
                                      title="Imprimir código de barras"
                                      aria-label="Imprimir código de barras"
                                    >
                                      <IconBarcode />
                                    </Link>
                                  </div>
                                </td>
                              </tr>
                            );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {moveModalOpen && activoToMove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Cambiar área del activo</h3>
            <p className="text-sm text-gray-600 mb-4">
              {activoToMove.code} — {activoToMove.name}
            </p>
            <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4">
              Área actual:{' '}
              <strong>{activoToMove.area?.name || 'Sin área asignada'}</strong>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nueva área *</label>
            <select
              value={newAreaId}
              onChange={(e) => setNewAreaId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-6"
            >
              <option value="">Seleccione un área…</option>
              {areas
                .filter((a) => String(a.id) !== String(activoToMove.area_id))
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.code ? ` (${a.code})` : ''}
                  </option>
                ))}
            </select>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeMoveModal}
                className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={savingMove}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmMoveArea}
                disabled={savingMove || !newAreaId}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {savingMove ? 'Guardando…' : 'Confirmar traslado'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default ActivosEnUso;
